import { useCallback, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'
import {
  fetchClinics,
  fetchPostcodes,
  fetchPricing,
  setSelectedPostcode,
  setSelectedClinic,
  setPostcodeInput,
  clearPostcodeSuggestions,
  setSelectedProcedure,
} from '../../actions/BillingGuideActions/billingGuideActions'
import {
  BillingGuideState,
  PostcodeData,
  PostcodeProps,
  ClinicProps,
  ResultsProps,
  ProcedureProps,
} from '../../domain/models/pages/BillingGuideLookupPage/BillingGuideLookupModel'
import { selectPostcodeProps, selectClinicProps, selectResultsProps } from '../../selectors/billingGuide'
import { billingGuideProcedures } from '../../_constants/text'

interface UseBillingGuideFormReturn {
  procedures: ProcedureProps
  postcode: PostcodeProps
  clinic: ClinicProps
  results: ResultsProps
}

const useBillingGuideForm = (): UseBillingGuideFormReturn => {
  const dispatch: Dispatch<AnyAction> = useDispatch()
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // === SELECTORS (Data from Redux) ===
  const billingGuideState = useSelector((state: { billingGuide: BillingGuideState }) => state.billingGuide)
  const postcodeData = useSelector((state: { billingGuide: BillingGuideState }) =>
    selectPostcodeProps(state.billingGuide),
  )
  const clinicData = useSelector((state: { billingGuide: BillingGuideState }) => selectClinicProps(state.billingGuide))
  const resultsData = useSelector((state: { billingGuide: BillingGuideState }) =>
    selectResultsProps(state.billingGuide),
  )

  // === EVENT HANDLERS ===
  // Postcode handlers
  const handlePostcodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      dispatch(setPostcodeInput(value))

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce postcode search (only search after 2 characters & 300ms delay)
      if (value.length >= 2) {
        searchTimeoutRef.current = setTimeout(() => {
          dispatch(fetchPostcodes(value))
        }, 300)
      } else {
        dispatch(clearPostcodeSuggestions())
      }
    },
    [dispatch],
  )

  const handlePostcodeSelect = useCallback(
    (item: PostcodeData) => {
      dispatch(setSelectedPostcode(item))
      dispatch(setPostcodeInput(`${item.postcode} - ${item.suburb}`))
      dispatch(clearPostcodeSuggestions())

      // Auto-fetch clinics when postcode is selected
      dispatch(fetchClinics(item.lat.toString(), item.lon.toString()))
    },
    [dispatch],
  )

  const handlePostcodeCloseDropdown = useCallback(() => {
    dispatch(clearPostcodeSuggestions())
  }, [dispatch])

  // Combine postcode data with handlers to create full prop object
  const postcode: PostcodeProps = useMemo(
    () => ({
      ...postcodeData,
      onChange: handlePostcodeChange,
      onSelect: handlePostcodeSelect,
      onCloseDropdown: handlePostcodeCloseDropdown,
    }),
    [postcodeData, handlePostcodeChange, handlePostcodeSelect, handlePostcodeCloseDropdown],
  )

  // Clinic handler
  const handleClinicSelect = useCallback(
    (displayValue: string) => {
      dispatch(setSelectedClinic(displayValue))
    },
    [dispatch],
  )

  // Procedure change handler
  const handleProcedureChange = useCallback(
    (procedureName: string) => {
      dispatch(setSelectedProcedure(procedureName))
    },
    [dispatch],
  )

  // Search handler (main action)
  const handleSearch = useCallback(() => {
    const selectedClinicValue = billingGuideState.selectedClinic
    const selectedProcedure = billingGuideState.selectedProcedure.toLowerCase().replace(/[^a-z0-9]+/g, '-')

    if (selectedClinicValue && selectedProcedure) {
      dispatch(fetchPricing(selectedClinicValue, selectedProcedure))
    }
  }, [dispatch, billingGuideState.selectedClinic, billingGuideState.selectedProcedure])

  // Combine procedure data for full prop object
  const procedures: ProcedureProps = useMemo(
    () => ({
      items: billingGuideProcedures,
      selectedItem: billingGuideState.selectedProcedure,
      onSelect: handleProcedureChange,
    }),
    [billingGuideState.selectedProcedure, handleProcedureChange],
  )

  // Combine clinic data with handlers to create full prop object
  const clinic: ClinicProps = useMemo(
    () => ({
      ...clinicData,
      onSelect: handleClinicSelect,
    }),
    [clinicData, handleClinicSelect],
  )

  // Combine search results with handler to create full prop object
  const results: ResultsProps = useMemo(
    () => ({
      ...resultsData,
      onSearch: handleSearch,
    }),
    [resultsData, handleSearch],
  )

  // === RETURN ===
  return {
    procedures,
    postcode,
    clinic,
    results,
  }
}

export default useBillingGuideForm
